import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { useAppRoles } from '../hooks/useAppRoles';
import { FiShield, FiPlus, FiEdit, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import LoadingButton from './LoadingButton';
import MoreMenu from './MoreMenu';
import { appAlert, appConfirm } from '../utils/appDialog';
import ListPager from './ListPager';
import { useClientPager } from '../hooks/useClientPager';

function slugFromLabel(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 32);
}

const RolesManagement = () => {
  const queryClient = useQueryClient();
  const { data: roles = [], isLoading } = useAppRoles({ includeAll: true });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    label: '',
    slug: '',
    description: '',
    grants_candidate_profile: false,
    sort_order: 100,
    clone_from_role: '',
    is_active: true,
  });

  const { data: allPermissions = [] } = useQuery('permissions', () =>
    api.get('/permissions').then((res) => res.data)
  );

  const createMutation = useMutation(
    (payload) => api.post('/roles', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('app-roles');
        setShowForm(false);
        resetForm();
        void appAlert('Role created. Set default permissions under Access control → Role defaults.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => api.put(`/roles/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('app-roles');
        setEditingId(null);
        resetForm();
        setShowForm(false);
        void appAlert('Role updated');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteMutation = useMutation((id) => api.delete(`/roles/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries('app-roles');
      void appAlert('Role deleted');
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const resetForm = () => {
    setForm({
      label: '',
      slug: '',
      description: '',
      grants_candidate_profile: false,
      sort_order: 100,
      clone_from_role: '',
      is_active: true,
    });
  };

  const startCreate = () => {
    setEditingId(null);
    resetForm();
    setShowForm(true);
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setForm({
      label: role.label,
      slug: role.slug,
      description: role.description || '',
      grants_candidate_profile: Boolean(role.grants_candidate_profile),
      sort_order: role.sort_order ?? 100,
      clone_from_role: '',
      is_active: role.is_active !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      label: form.label.trim(),
      description: form.description.trim() || null,
      grants_candidate_profile: form.grants_candidate_profile,
      sort_order: Number(form.sort_order) || 100,
    };
    if (editingId) {
      payload.is_active = form.is_active;
      updateMutation.mutate({ id: editingId, payload });
      return;
    }
    payload.slug = form.slug.trim() || slugFromLabel(form.label);
    if (form.clone_from_role) payload.clone_from_role = form.clone_from_role;
    createMutation.mutate(payload);
  };

  const sortedRoles = useMemo(
    () => [...roles].sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100)),
    [roles]
  );

  const {
    pageItems: pagedRoles,
    pagination: rolesPagination,
    setPage: setRolesPage,
  } = useClientPager(sortedRoles);

  if (isLoading) {
    return <div className="loading">Loading roles…</div>;
  }

  return (
    <div className="settings-section roles-management">
      <div className="section-header-with-action">
        <h2>
          <FiShield /> Application roles
        </h2>
        <button type="button" className="btn btn-primary" onClick={startCreate}>
          <FiPlus /> Add role
        </button>
      </div>

      <p className="roles-management-intro">
        Define roles for your organization. Assign users a role in{' '}
        <strong>Users</strong> or on the Users page. Configure what each role can do under{' '}
        <Link to="/settings?tab=permissions" className="scanner-inline-link">
          Access control → Role defaults
        </Link>
        .
      </p>

      <div className="roles-table-wrap">
        <table className="table roles-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Slug</th>
              <th>Users</th>
              <th>Candidate profile</th>
              <th>App login</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedRoles.map((role) => (
              <tr key={role.id}>
                <td>
                  <strong>{role.label}</strong>
                  {role.is_builtin && <span className="badge badge-info roles-builtin-badge">Built-in</span>}
                  {role.description && (
                    <div className="roles-description">{role.description}</div>
                  )}
                </td>
                <td>
                  <code>{role.slug}</code>
                </td>
                <td>{role.user_count ?? 0}</td>
                <td>{role.grants_candidate_profile ? 'Yes' : 'No'}</td>
                <td>{role.allows_login === false ? 'No' : 'Yes'}</td>
                <td>
                  <span className={`badge badge-${role.is_active ? 'success' : 'danger'}`}>
                    {role.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="roles-actions">
                  <MoreMenu
                    label={`Actions for ${role.label}`}
                    size="sm"
                    items={[
                      {
                        id: 'edit',
                        label: 'Edit',
                        icon: FiEdit,
                        onSelect: () => startEdit(role),
                      },
                      {
                        id: 'delete',
                        label: 'Delete',
                        icon: FiTrash2,
                        danger: true,
                        hidden: role.is_builtin || (role.user_count ?? 0) > 0,
                        onSelect: async () => {
                          if (await appConfirm(`Delete role "${role.label}"?`)) {
                            deleteMutation.mutate(role.id);
                          }
                        },
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPager
        page={rolesPagination.page}
        pageCount={rolesPagination.pageCount}
        total={rolesPagination.total}
        limit={rolesPagination.limit}
        onPageChange={setRolesPage}
      />

      {showForm && (
        <div className="roles-form-card">
          <h3>{editingId ? 'Edit role' : 'New role'}</h3>
          <form onSubmit={handleSubmit} className="roles-form">
            <div className="form-group">
              <label>Display name *</label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    label,
                    slug: editingId ? prev.slug : slugFromLabel(label),
                  }));
                }}
                required
                placeholder="e.g. Recruiter"
              />
            </div>
            {!editingId && (
              <div className="form-group">
                <label>Slug (stored on user accounts)</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="recruiter"
                  pattern="[a-z][a-z0-9_]{1,31}"
                />
                <p className="form-hint">Lowercase letters, numbers, underscores. Cannot change after creation.</p>
              </div>
            )}
            {editingId && (
              <div className="form-group">
                <label>Slug</label>
                <input type="text" value={form.slug} disabled />
              </div>
            )}
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Optional description for admins"
              />
            </div>
            {!editingId && roles.length > 0 && allPermissions.length > 0 && (
              <div className="form-group">
                <label>Clone permissions from</label>
                <select
                  value={form.clone_from_role}
                  onChange={(e) => setForm({ ...form, clone_from_role: e.target.value })}
                >
                  <option value="">— Start with no permissions —</option>
                  {roles.filter((r) => r.is_active).map((r) => (
                    <option key={r.slug} value={r.slug}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>Sort order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  min={0}
                />
              </div>
              {editingId && (
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={form.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={form.grants_candidate_profile}
                  onChange={(e) => setForm({ ...form, grants_candidate_profile: e.target.checked })}
                />
                {' '}Create candidate profile for users with this role
              </label>
              <p className="form-hint">
                Same as the built-in Candidate role — enables resume upload, matches, and candidate workflows.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
              >
                <FiX /> Cancel
              </button>
              <LoadingButton
                type="submit"
                className="btn btn-primary"
                icon={FiSave}
                loading={createMutation.isLoading || updateMutation.isLoading}
                loadingLabel="Saving…"
              >
                {editingId ? 'Save role' : 'Create role'}
              </LoadingButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
